import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  Fab,
  CircularProgress,
  Button,
} from '@mui/material';
import { Send as SendIcon, Close as CloseIcon } from '@mui/icons-material';
import { useCopilot } from './useCopilot';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { useLocation } from 'react-router-dom';
import {
  copilotFallbackMessage,
  defaultQuestions,
  routeComponentMap,
  suggestedQuestionsMap,
} from '../../appConstants';
import { updateCopilotFilters } from '../../slices/CopilotSlice/copilotSlice';
import { CopilotFilters } from './interface';
import { formatMessageWithBold } from '../../common/utils';
import { setCopilotOpen, setCopilotDisabled } from '../../slices/CopilotSlice/copilotSlice';

interface CopilotProps {
  defaultOpen?: boolean;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
}

const Copilot: React.FC<CopilotProps> = ({
  // defaultOpen = false,
  position = 'bottom-right',
}) => {
  // const [isOpen, setIsOpen] = useState(defaultOpen);
  const [inputValue, setInputValue] = useState('');
  const [filterAnimationStep, setFilterAnimationStep] = useState<
    'idle' | 'searching' | 'applying' | 'done'
  >('idle');
  const [showActionButtons, setShowActionButtons] = useState(false);
  const [messageFilters, setMessageFilters] = useState<CopilotFilters>({
    industries: [],
    functions: [],
    impact: [],
    benefitAreas: [],
  });

  const isOpen = useSelector((state: RootState) => state.copilotFilters.isOpen);
  const isCopilotDisabled = useSelector(
    (state: RootState) => state.copilotFilters.isCopilotDisabled
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const dispatch = useDispatch();
  const { first_name } = useSelector((state: RootState) => state.user);
  const { messages, isLoading, sendMessage, clearMessages } = useCopilot();

  const location = useLocation();
  const currentPath = location.pathname;
  const questions = suggestedQuestionsMap[currentPath] || defaultQuestions;
  const componentName = routeComponentMap.get(currentPath) || 'Industry XPLR';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCloseIconClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Prevent the FAB click event
    dispatch(setCopilotDisabled(true));
    dispatch(setCopilotOpen(false)); // Close the copilot
  };

  useEffect(() => {
    scrollToBottom();
    const last = messages[messages.length - 1];
    if (
      last?.role === 'copilot' &&
      last.command?.actionable &&
      last.command?.payload &&
      Array.isArray(last.command.payload.industries) &&
      Array.isArray(last.command.payload.functions) &&
      Array.isArray(last.command.payload.impact) &&
      Array.isArray(last.command.payload.benefitAreas)
    ) {
      setShowActionButtons(true);
      setMessageFilters({
        industries: last.command.payload.industries,
        functions: last.command.payload.functions,
        impact: last.command.payload.impact,
        benefitAreas: last.command.payload.benefitAreas,
      });
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    const message = inputValue.trim();
    setInputValue('');
    await sendMessage(message);
  };

  const handleSuggestedQuestion = (question: string) => {
    setInputValue(question);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleActionYes = async () => {
    setShowActionButtons(false);
    setFilterAnimationStep('searching');

    setTimeout(() => {
      setFilterAnimationStep('applying');
      dispatch(updateCopilotFilters(messageFilters));

      setTimeout(() => {
        setFilterAnimationStep('done');
        setTimeout(() => {
          setFilterAnimationStep('idle');
          setShowActionButtons(false);
        }, 250);
      }, 500);
    }, 500);
  };

  const handleActionNo = () => {
    setShowActionButtons(false);
  };

  const getPositionStyles = () => {
    const styles: React.CSSProperties = { position: 'fixed', zIndex: 1300 };
    switch (position) {
      case 'bottom-right':
        return { ...styles, bottom: 60, right: 20 };
      case 'bottom-left':
        return { ...styles, bottom: 60, left: 20 };
      case 'top-right':
        return { ...styles, top: 60, right: 20 };
      case 'top-left':
        return { ...styles, top: 60, left: 20 };
      default:
        return { ...styles, bottom: 60, right: 20 };
    }
  };

  const getChatWindowStyles = () => {
    const styles: React.CSSProperties = { position: 'fixed', zIndex: 1300 };
    switch (position) {
      case 'bottom-right':
        return { ...styles, bottom: 90, right: 20 };
      case 'bottom-left':
        return { ...styles, bottom: 90, left: 20 };
      case 'top-right':
        return { ...styles, top: 90, right: 20 };
      case 'top-left':
        return { ...styles, top: 90, left: 20 };
      default:
        return { ...styles, bottom: 90, right: 20 };
    }
  };

  const handleNewChatClick = () => {
    clearMessages();
    setFilterAnimationStep('idle');
  };

  return (
    <>
      {isOpen && (
        <Box sx={getChatWindowStyles()}>
          <Paper
            elevation={0}
            sx={{
              width: { xs: 300, sm: 380 },
              height: { xs: 400, sm: 600 },
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: 'white',
              border: '1px solid #e5e7eb',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                height: 48,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img src="images/ai-xplr-logo-icon.svg" alt="copilot" width={16} height={16} />
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1A55F9' }}>
                  AI XPLR™
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
                  Copilot
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={handleNewChatClick}
                sx={{
                  bgcolor: '#111827',
                  color: 'white',
                  fontSize: 12,
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  '&:hover': { bgcolor: '#374151' },
                }}
              >
                New Chat
              </Button>
            </Box>

            {/* Body */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#f9fafb' }}>
              {messages.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography
                    sx={{
                      color: '#111827',
                      fontWeight: 500,
                      mb: 3,
                      fontSize: 16,
                    }}
                  >
                    Hi {first_name || 'User'} 👋, I am AI XPLR Copilot, ask me anything about{' '}
                    <Box
                      component="span"
                      sx={{ fontWeight: 600, color: '#1a55f9', display: 'inline' }}
                    >
                      {componentName}
                    </Box>
                    !
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    {questions.map((q, i) => (
                      <Button
                        key={i}
                        variant="outlined"
                        onClick={() => handleSuggestedQuestion(q)}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#e5e7eb',
                          color: '#374151',
                          bgcolor: 'white',
                          py: 1.5,
                          px: 2,
                          borderRadius: 2,
                          fontSize: 14,
                          fontWeight: 400,
                          width: 'fit-content',
                          maxWidth: '100%',
                          '&:hover': {
                            borderColor: '#3b82f6',
                            bgcolor: '#f8fafc',
                          },
                        }}
                      >
                        {q}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}

              <List sx={{ p: 0 }}>
                {messages.map((m) => {
                  const isUser = m.role === 'user';

                  return (
                    <ListItem
                      key={m.id}
                      sx={{
                        flexDirection: 'column',
                        alignItems: isUser ? 'flex-end' : 'flex-start',
                        p: 0,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          p: 2,
                          maxWidth: '85%',
                          bgcolor: isUser ? '#d9e7ff' : '#f7f7f7',
                          color: '#111827',
                          borderRadius: 2,
                          border: '1px solid #f0f0f0',
                          fontSize: 14,
                          mr: isUser ? '0px' : 'auto',
                          ml: isUser ? 'auto' : '0px',
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            [isUser ? 'right' : 'left']: -5,
                            width: 0,
                            height: 0,
                            borderTop: '6px solid transparent',
                            borderBottom: '6px solid transparent',
                            borderLeft: isUser ? '6px solid #d9e7ff' : 'none',
                            borderRight: !isUser ? '6px solid #f7f7f7' : 'none',
                          }}
                        />
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {typeof m.content === 'string'
                            ? formatMessageWithBold(m.content)
                            : !isUser
                              ? formatMessageWithBold(copilotFallbackMessage)
                              : m.content}
                        </Typography>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>

              {isLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: '#3b82f6',
                        animation: 'blink 1s infinite 0s ease-in-out',
                      }}
                    />
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: '#3b82f6',
                        animation: 'blink 1s infinite 0.2s ease-in-out',
                      }}
                    />
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: '#3b82f6',
                        animation: 'blink 1s infinite 0.4s ease-in-out',
                      }}
                    />
                  </Box>
                </Box>
              )}

              {filterAnimationStep !== 'idle' && filterAnimationStep !== 'done' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                  <CircularProgress size={16} sx={{ color: '#3b82f6' }} />
                  <Typography sx={{ color: '#6b7280', fontSize: 13 }}>
                    {filterAnimationStep === 'searching'
                      ? 'Searching for filters...'
                      : 'Applying filters...'}
                  </Typography>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Footer */}
            <Box sx={{ flexShrink: 0, p: 2, borderTop: '1px solid #e5e7eb', bgcolor: 'white' }}>
              {showActionButtons && filterAnimationStep === 'idle' && (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleActionYes}
                    sx={{ textTransform: 'none' }}
                  >
                    Yes
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleActionNo}
                    sx={{ textTransform: 'none' }}
                  >
                    No
                  </Button>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  inputRef={inputRef}
                  fullWidth
                  multiline
                  maxRows={3}
                  variant="outlined"
                  size="small"
                  placeholder="Type your message..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading || filterAnimationStep !== 'idle'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#f9fafb',
                      fontSize: 14,
                      '& fieldset': { borderColor: '#e5e7eb' },
                      '&:hover fieldset': { borderColor: '#3b82f6' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                    },
                  }}
                />
                <IconButton
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isLoading || filterAnimationStep !== 'idle'}
                  sx={{
                    bgcolor: 'white',
                    color: 'black',
                    width: 40,
                    height: 40,
                    '&:hover': { bgcolor: '#e7e9ea' },
                    '&:disabled': { color: '#9ca3af' },
                  }}
                >
                  <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'center',
                  color: '#9ca3af',
                  fontSize: 11,
                  mt: 1,
                }}
              >
                powered by ZBrain
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {!isCopilotDisabled && (
        <Box sx={getPositionStyles()}>
          <Fab
            onClick={() => dispatch(setCopilotOpen(!isOpen))}
            sx={{
              width: 56,
              height: 56,
              bgcolor: '#131426',
              color: 'white',
              '&:hover': { bgcolor: '#1f1f3a' },
              boxShadow: '0 8px 32px rgba(19, 20, 38, 0.3)',
              position: 'relative',
            }}
          >
            {isOpen ? (
              <CloseIcon sx={{ fontSize: 28 }} />
            ) : (
              <>
                <img src="images/copilot-icon1.svg" alt="copilot" width={28} height={28} />
                <Box
                  onClick={handleCloseIconClick}
                  sx={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    width: 20,
                    height: 20,
                    bgcolor: '#9ca3af',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    border: '2px solid white',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: '#6b7280',
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 12, color: 'white' }} />
                </Box>
              </>
            )}
          </Fab>
        </Box>
      )}

      {/* <Box sx={getPositionStyles()}>
        <Fab
          onClick={() => dispatch(setCopilotOpen(!isOpen))}
          sx={{ 
            width: 56, 
            height: 56, 
            bgcolor: '#131426', 
            color: 'white', 
            '&:hover': { bgcolor: isCopilotDisabled ? '#6b7280' : '#1f1f3a' }, 
              boxShadow: '0 8px 32px rgba(19, 20, 38, 0.3)',
              position: 'relative',
              '&:disabled': {
                bgcolor: '#6b7280',
                color: '#9ca3af'
              }
            }}
        >
          {isOpen ? (
            // Image 2: When chat is open, show only large cross icon
            <CloseIcon sx={{ fontSize: 28 }} />
          ) : (
            // Image 1: When chat is closed, show chat bot icon with cross overlay
            <>
              <img src='images/copilot-icon1.svg' alt='copilot' width={28} height={28} />
              <Box
                onClick={handleCloseIconClick}
                sx={{
                  position: 'absolute',
                  top: -5,
                  right: -5,
                  width: 20,
                  height: 20,
                  bgcolor: '#9ca3af',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                  border: '2px solid white',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: '#6b7280'
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: 12, color: 'white' }} />
              </Box>
            </>
          )}
        </Fab>
      </Box> */}

      {/* Keyframes */}
      <style>
        {`
          @keyframes blink {
            0%, 80%, 100% { opacity: 0; }
            40% { opacity: 1; }
          }
        `}
      </style>
    </>
  );
};

export default Copilot;
